"use client"

import { useState, useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { Header } from './header'
import { Sidebar } from './sidebar'
import { cn } from '@/lib/utils'
import { useSessionExtension } from '@/lib/hooks/use-session-extension'

interface MainLayoutProps {
  children: React.ReactNode
}

export function MainLayout({ children }: MainLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  // Automatically extend sessions on user activity
  useSessionExtension()

  // Close mobile sidebar when the route/path changes
  const pathname = usePathname()
  useEffect(() => {
    // Whenever navigation occurs, ensure mobile drawer is closed to avoid
    // leaving it open when the user navigates to a new page.
    setMobileSidebarOpen(false)
  }, [pathname])

  return (
    <div className="min-h-screen bg-background">
      <Header
        onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
        onToggleSidebarMobile={() => setMobileSidebarOpen((v) => !v)}
        sidebarCollapsed={sidebarCollapsed}
      />

      <div className="flex">
        {/* Sidebar (portal): let Sidebar handle its own positioning; pass mobileOpen state */}
        <Sidebar collapsed={sidebarCollapsed} mobileOpen={mobileSidebarOpen} onCloseMobile={() => setMobileSidebarOpen(false)} />

        {/* Overlay comes after the sidebar in DOM but has a lower z so it appears beneath the sidebar */}
        {mobileSidebarOpen && (
          <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={() => setMobileSidebarOpen(false)} aria-hidden />
        )}

        <main
          className={cn(
            "flex-1 transition-all duration-200 ease-in-out pt-16 overflow-x-hidden",
            sidebarCollapsed ? "md:ltr:ml-16 md:rtl:mr-16" : "md:ltr:ml-64 md:rtl:mr-64"
          )}
        >
          {children}
        </main>
      </div>
    </div>
  )
}